import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { PatternBackground } from "@/app/icons";
import { isProduction } from "@/app/lib/constants";
import {
  Feature,
  isFeatureDisabled,
  isFeatureEnabled,
} from "@/app/lib/utils/featureFlags";
import { FlightCalculator } from "@/app/ui/flights/FlightCalculator";
import Footer from "@/app/ui/Footer";
import HotelsCalculator from "@/app/ui/hotels/HotelsCalculator";
import { ScopeSwitcher } from "@/app/ui/ScopeSwitcher";
import { OffersCarousel } from "@/app/ui/offers/OffersCarousel";
import { MainSearchWhatsAppBanner } from "@/app/ui/WhatsAppBanners";
import type { SearchScope } from "@/app/lib/types/types";
import { TripList } from "@/app/ui/trips/TripList";
import { useFlightSearchForm } from "@/app/lib/hooks/flightSearch/useFlightSearchForm";

const defaultCategory: SearchScope = "flights";

export const Route = createFileRoute("/_authenticated/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { category?: SearchScope } => {
    if (
      (isFeatureDisabled(Feature.HotelsFlow) && search.category === "hotels") ||
      (isFeatureDisabled(Feature.TripsFlow) && search.category === "trips")
    ) {
      return {
        category: undefined,
      };
    }

    return {
      category: search.category as SearchScope,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { category } = Route.useSearch();
  const navigate = Route.useNavigate();
  const t = useTranslations();

  const { handleSubmit } = useFlightSearchForm();

  return (
    <main className="main">
      <div className="relative flex w-full items-center justify-center overflow-clip bg-surface-accent-stronger pt-4 pb-10">
        <PatternBackground aria-hidden className="absolute inset-0" />

        {!isProduction ? (
          <div className="absolute start-2 top-2 rounded-full bg-surface-accent-strong px-3 py-2 text-xs text-white capitalize">
            {import.meta.env.VITE_PUBLIC_ENVIRONMENT}
          </div>
        ) : null}

        <ScopeSwitcher current={category || defaultCategory} />
      </div>

      <div className="relative -mt-6 rounded-t-4xl bg-surface-background px-4 pt-6 pb-4">
        {category === "hotels" ? (
          <HotelsCalculator
            submitButtonContent={t("hotels.search.searchHotel")}
            onAfterSubmit={() => {
              navigate({ to: "/hotels/list" });
            }}
          />
        ) : category === "trips" ? (
          <TripList />
        ) : (
          <FlightCalculator
            submitButtonContent={t("flights.search.searchFlight")}
            onSubmit={handleSubmit}
            onAfterSubmit={() => {
              navigate({ to: "/flights/departure/list" });
            }}
            analyticsSource="main"
          />
        )}
      </div>

      {isFeatureEnabled(Feature.HomePageBanners) ? <OffersCarousel /> : null}

      {category !== "trips" ? (
        <div className="mt-1 px-4 pb-20">
          <MainSearchWhatsAppBanner />
        </div>
      ) : null}

      <Footer />
    </main>
  );
}
